/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { JobDrawsRestService, JobRestService, PrinterRestService, reportIdField } from '@gh/builder-shared-data';
import { getFormStateSnapshotValue, getRouteParam, InitViewService, IS_REGION_US } from '@gh/common-shared';
import { ID, ProduceStatus } from '@gh/common-shared-data';
import { loadTinymce } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { combineLatest$, of$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  DRAW_DETAILS_INIT,
  DrawCalculateResponse,
  DrawDetails,
  drawDetailsForInvoiceFromDto,
  drawDetailsForMakeFromDto,
  drawDetailsToCalculateRequestDto,
  drawDetailsToCreateLendersScheduleRequestDto,
  drawDetailsToCreatePaymentScheduleRequestDto,
  DrawGeneratedDocs,
  DrawPrintRequest,
  mergeDrawDetailsAmountWithGst,
  queryDrawDetailsIsLendersSchedule,
} from './draw-details.model';
import { JobBasicInfo, REPORT_JOB_DETAILS, ReportService } from '@gh/builder-shared';

function getJobDetails(route: ActivatedRouteSnapshot): DrawDetails {
  if (route.parent) {
    return route.parent.data.init['details'];
  } else {
    throw new Error('DrawDetailsTabBusinessService: route parent is not defined');
  }
}

function getTinymce(route: ActivatedRouteSnapshot): DrawDetails {
  if (route.parent) {
    return route.parent.data.init['tinymce'];
  } else {
    throw new Error('DrawDetailsTabBusinessService: route parent is not defined');
  }
}

@Injectable()
export class DrawDetailsBusinessService implements InitViewService {
  jobId: ID;
  basicInfo: JobBasicInfo;

  constructor(private gridLayouts: GridLayoutService,
              private jobDrawsRest: JobDrawsRestService,
              private printerRest: PrinterRestService,
              private jobRest: JobRestService,
              private report: ReportService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const jobId = getRouteParam(route, 'jobId');
    this.jobId = jobId;
    return {
      isCreateMode: true,
      details: jobId ? this.getForJob(jobId) : DRAW_DETAILS_INIT,
      tinymce: loadTinymce(),
      basicInfo: this.getBasicInfo().pipe(map$((info) => {
        this.basicInfo = info;
        return info;
      })),
      reportUrl: this.report.generateUrl(REPORT_JOB_DETAILS, [reportIdField('Job', jobId)]),
    };
  }

  initMakeView(route: ActivatedRouteSnapshot): any {
    // @ts-ignore
    const jobId = getRouteParam(route.parent, 'jobId');
    this.jobId = jobId;
    return {
      isCreateMode: true,
      details: jobId ? this.getForJob(jobId) : DRAW_DETAILS_INIT,
      changeOrderGridLayout: this.gridLayouts.get('drawChangeOrders'),
      includedLendersCostCentersGridLayout: this.gridLayouts.get('includedLendersCostCenters'),
      tinymce: getTinymce(route),
    };
  }

  initInvoiceView(route: ActivatedRouteSnapshot): any {
    const jobId = getRouteParam(route, 'jobId');
    const invoiceId = getRouteParam(route, 'invoiceId');

    return {
      isCreateMode: false,
      details: this.getForInvoice(jobId, invoiceId),
      changeOrderGridLayout: this.gridLayouts.get('drawChangeOrders'),
      includedLendersCostCentersGridLayout: this.gridLayouts.get('includedLendersCostCenters'),
    };
  }

  getForJob(jobId: ID): Observable<DrawDetails> {
    return combineLatest$(
      this.jobDrawsRest.init(jobId),
      this.jobDrawsRest.getProduceStatus(jobId),
      this.printerRest.findAll()).pipe(
      map$(([details, produceStatus, { isCloud }]) =>
             drawDetailsForMakeFromDto(details, produceStatus, isCloud)),
      switchMap$((details) =>
        this.calculateGst(details).pipe(map$((response) =>
                                               mergeDrawDetailsAmountWithGst(details, response)))));
  }

  getForInvoice(jobId: ID, invoiceId: ID): Observable<DrawDetails> {
    return combineLatest$(
      this.jobDrawsRest.get(jobId, invoiceId),
      this.jobDrawsRest.getProduceStatus(jobId, invoiceId),
      this.printerRest.findAll()).pipe(
      map$(([details, produceStatus, { isCloud }]) =>
             drawDetailsForInvoiceFromDto(details, invoiceId, produceStatus, isCloud)));
  }

  create(details: DrawDetails, historyAreaMemo?: string): Observable<void> {
    return queryDrawDetailsIsLendersSchedule(details)
      ? this.jobDrawsRest.createLendersSchedule(
        details.job.jobId,
        drawDetailsToCreateLendersScheduleRequestDto(details))
      : this.jobDrawsRest.createPaymentSchedule(
        details.job.jobId,
        drawDetailsToCreatePaymentScheduleRequestDto(details, historyAreaMemo || ''));
  }

  calculateGst(details: DrawDetails): Observable<DrawCalculateResponse> {
    return IS_REGION_US
      ? of$({
        thisDrawAmountWithGst: getFormStateSnapshotValue(details.paymentProgressInfo).thisDraw,
        recommendedAmountWithGst: getFormStateSnapshotValue(details.paymentProgressInfo).recommendedDrawAmountWithGst,
      })
      : combineLatest$(
        this.jobDrawsRest.calculateGst(
          details.job.jobId,
          drawDetailsToCalculateRequestDto(details, 'thisDraw')).pipe(map$(({ amountWithGst }) => amountWithGst)),
        this.jobDrawsRest.calculateGst(
          details.job.jobId,
          drawDetailsToCalculateRequestDto(details, 'recommendedDraw')).pipe(
          map$(({ amountWithGst }) => amountWithGst)),
      ).pipe(
        map$(([thisDrawAmountWithGst, recommendedAmountWithGst]) => ({
          thisDrawAmountWithGst,
          recommendedAmountWithGst,
        })));
  }

  print(jobId: ID, request: DrawPrintRequest): Observable<any> {
    return this.jobDrawsRest.print(jobId, request);
  }

  produceDocs(jobId: ID, invoiceId: ID, isPreview: boolean): Observable<void> {
    return this.jobDrawsRest.produceDocs(jobId, invoiceId, isPreview);
  }

  getProduceStatus(jobId: ID, invoiceId?: ID): Observable<ProduceStatus> {
    return this.jobDrawsRest.getProduceStatus(jobId, invoiceId);
  }

  getGeneratedDocuments(jobId: ID, invoiceId?: ID): Observable<DrawGeneratedDocs> {
    return this.jobDrawsRest.getGeneratedDocuments(jobId, invoiceId);
  }

  getBasicInfo(): Observable<JobBasicInfo> {
    return this.jobRest.getBasic(this.jobId);
  }
}
